import planeUrl from "assets/svgs/alerts-plane.svg";
import { LuArrowLeft } from "react-icons/lu";
import { CardSlot, Stage, StageInner } from "../marketing.styles";
import {
  BackChip,
  Bottom,
  CardColumn,
  End,
  EndCity,
  EndCode,
  EndDate,
  Ends,
  Fact,
  FactText,
  FactTitle,
  Facts,
  HeroGrid,
  Intro,
  Lead,
  LeftColumn,
  LeftTop,
  Mark,
  Plane,
  PriceBadge,
  PriceLabel,
  PriceValue,
  Route,
  RouteBox,
  RoutePath,
  RouteSvg,
  Title,
} from "./styles";
import type { AlertFormHeroProps } from "./types";

const FACTS = [
  { title: "Grátis", text: "e sem compromisso" },
  { title: "E-mail ou push", text: "no app, como preferir" },
  { title: "Pausa quando quiser", text: "ou apaga o alerta" },
];

/**
 * Hero das telas de cadastro e edição de alerta.
 *
 * Esquerda: título grande com a frase-chave em faixa, mapa da rota (o arco
 * liga origem, preço e destino) e um rodapé com os benefícios. Direita: o
 * card do formulário, recebido por `children`.
 *
 * @param mode - "create" para o cadastro, "edit" para a edição.
 * @param backTo - Rota do link "Voltar para meus alertas".
 * @param alert - Resumo do alerta existente (somente no modo "edit").
 * @param children - Card do formulário.
 */
export const AlertFormHero: React.FC<AlertFormHeroProps> = ({
  mode,
  backTo,
  alert,
  children,
}) => {
  const isEdit = mode === "edit" && alert !== undefined;

  return (
    <Stage>
      <StageInner>
        <HeroGrid>
          <LeftColumn>
            <LeftTop>
              <BackChip to={backTo}>
                <LuArrowLeft aria-hidden="true" />
                Voltar para meus alertas
              </BackChip>

              <Intro>
                <Title>
                  {isEdit ? (
                    <>
                      Ajuste <Mark>seu alerta.</Mark>
                    </>
                  ) : (
                    <>
                      Pare de caçar passagem.
                      <br />
                      <Mark>A gente te avisa.</Mark>
                    </>
                  )}
                </Title>
                <Lead>
                  {isEdit
                    ? "Atualize a rota, as datas, o preço máximo ou a forma de aviso."
                    : "Diga o preço que cabe no seu bolso. Quando a passagem chegar nele, você fica sabendo."}
                </Lead>
              </Intro>

              <Route>
                <RouteBox aria-hidden="true">
                  <RouteSvg
                    viewBox="0 0 520 150"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <RoutePath d="M14 132 Q 260 -36 506 132" />
                    {isEdit ? (
                      <circle cx="14" cy="132" r="9" fill="#fff" />
                    ) : (
                      <circle
                        cx="14"
                        cy="132"
                        r="8"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="4"
                      />
                    )}
                    <circle
                      cx="506"
                      cy="132"
                      r="10"
                      fill="#17415A"
                      stroke="#fff"
                      strokeWidth="4"
                    />
                  </RouteSvg>
                  <Plane src={planeUrl} alt="" />
                </RouteBox>
                <Ends>
                  <End>
                    {isEdit ? (
                      <>
                        <EndCode>{alert.originCode}</EndCode>
                        <EndCity>{alert.originLabel}</EndCity>
                        <EndDate>{alert.departureNote}</EndDate>
                      </>
                    ) : (
                      <>
                        <EndCode $ghost>De onde?</EndCode>
                        <EndCity>Você escolhe</EndCity>
                      </>
                    )}
                  </End>
                  <PriceBadge>
                    <PriceLabel>Preço máximo</PriceLabel>
                    <PriceValue>{isEdit ? alert.price : "R$ ?"}</PriceValue>
                  </PriceBadge>
                  <End $right>
                    {isEdit ? (
                      <>
                        <EndCode>{alert.destinationCode}</EndCode>
                        <EndCity>{alert.destinationLabel}</EndCity>
                        <EndDate>{alert.returnNote}</EndDate>
                      </>
                    ) : (
                      <>
                        <EndCode $ghost>Para onde?</EndCode>
                        <EndCity>Datas ou qualquer data</EndCity>
                      </>
                    )}
                  </End>
                </Ends>
              </Route>
            </LeftTop>

            <Bottom>
              <Facts>
                {FACTS.map((fact) => (
                  <Fact key={fact.title}>
                    <FactTitle>{fact.title}</FactTitle>
                    <FactText>{fact.text}</FactText>
                  </Fact>
                ))}
              </Facts>
            </Bottom>
          </LeftColumn>

          <CardColumn>
            <CardSlot>{children}</CardSlot>
          </CardColumn>
        </HeroGrid>
      </StageInner>
    </Stage>
  );
};
